import { jsPDF } from 'jspdf';
import { WorshipReport } from '../types/worshipReport';
import { FinanceReport } from '../types/financeReport';
import { AdnReport } from '../types/adnReport';
import { AcademieReport } from '../types/academieReport';
import { SonoReport } from '../types/sonoReport';
import { SainteCeneReport } from '../types/sainteCeneReport';
import { formatNumber } from './format';

// ─── Couleurs VH ──────────────────────────────────────────────────────────────
const GREEN  = [0, 102, 92]   as [number, number, number]; // #00665C
const GOLD   = [242, 182, 54] as [number, number, number]; // #F2B636
const DARK   = [31, 41, 55]   as [number, number, number]; // gray-800
const GRAY   = [107, 114, 128]as [number, number, number]; // gray-500
const LGRAY  = [243, 244, 246]as [number, number, number]; // gray-100
const WHITE  = [255, 255, 255]as [number, number, number];

const CHURCH = "Vases d'Honneur Angré Djibi 3";
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 15;
const CONTENT_W = PAGE_W - MARGIN * 2;

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });
}

// ─── Constructeur de base ─────────────────────────────────────────────────────
class PdfBuilder {
  doc: jsPDF;
  y: number;

  constructor() {
    this.doc = new jsPDF({ unit: 'mm', format: 'a4' });
    this.y = 0;
  }

  /** En-tête de page commun */
  header(title: string, subtitle: string) {
    const d = this.doc;
    // Bande verte
    d.setFillColor(...GREEN);
    d.rect(0, 0, PAGE_W, 28, 'F');
    // Logo text
    d.setTextColor(...WHITE);
    d.setFontSize(8);
    d.setFont('helvetica', 'normal');
    d.text(CHURCH.toUpperCase(), MARGIN, 10);
    // Titre
    d.setFontSize(15);
    d.setFont('helvetica', 'bold');
    d.text(title, MARGIN, 20);
    // Trait or sous le header
    d.setFillColor(...GOLD);
    d.rect(0, 28, PAGE_W, 2, 'F');
    // Sous-titre
    d.setTextColor(...GRAY);
    d.setFontSize(9);
    d.setFont('helvetica', 'italic');
    d.text(subtitle, MARGIN, 37);
    this.y = 45;
  }

  /** Section avec titre */
  section(label: string) {
    this.y += 4;
    const d = this.doc;
    d.setFillColor(...GREEN);
    d.rect(MARGIN, this.y, 3, 5, 'F');
    d.setTextColor(...GREEN);
    d.setFontSize(10);
    d.setFont('helvetica', 'bold');
    d.text(label.toUpperCase(), MARGIN + 5, this.y + 4);
    this.y += 9;
    d.setDrawColor(...GREEN);
    d.setLineWidth(0.3);
    d.line(MARGIN, this.y, PAGE_W - MARGIN, this.y);
    this.y += 4;
  }

  /** Ligne clé : valeur */
  row(key: string, value: string, highlight = false) {
    const d = this.doc;
    this.checkPage();
    if (highlight) {
      d.setFillColor(...LGRAY);
      d.rect(MARGIN, this.y - 3, CONTENT_W, 7, 'F');
    }
    d.setTextColor(...GRAY);
    d.setFontSize(9);
    d.setFont('helvetica', 'normal');
    d.text(key, MARGIN + 2, this.y + 1);
    d.setTextColor(...DARK);
    d.setFont('helvetica', 'bold');
    d.text(String(value), MARGIN + 70, this.y + 1);
    this.y += 7;
  }

  /** Ligne de tableau (2 colonnes) */
  tableRow(col1: string, col2: string, isHeader = false, bgColor?: [number, number, number]) {
    const d = this.doc;
    this.checkPage();
    const bg = bgColor ?? (isHeader ? GREEN : LGRAY);
    if (isHeader || bgColor) {
      d.setFillColor(...bg);
      d.rect(MARGIN, this.y - 4, CONTENT_W, 7, 'F');
    }
    d.setTextColor(isHeader ? 255 : DARK[0], isHeader ? 255 : DARK[1], isHeader ? 255 : DARK[2]);
    d.setFontSize(9);
    d.setFont('helvetica', isHeader ? 'bold' : 'normal');
    d.text(col1, MARGIN + 2, this.y);
    d.text(col2, MARGIN + 120, this.y);
    this.y += 7;
  }

  /** Ligne de tableau 3 colonnes */
  table3Row(c1: string, c2: string, c3: string, isHeader = false) {
    const d = this.doc;
    this.checkPage();
    if (isHeader) {
      d.setFillColor(...GREEN);
      d.rect(MARGIN, this.y - 4, CONTENT_W, 7, 'F');
    } else {
      d.setFillColor(...LGRAY);
      d.rect(MARGIN, this.y - 4, CONTENT_W, 7, 'F');
    }
    d.setTextColor(isHeader ? 255 : DARK[0], isHeader ? 255 : DARK[1], isHeader ? 255 : DARK[2]);
    d.setFontSize(9);
    d.setFont('helvetica', isHeader ? 'bold' : 'normal');
    d.text(c1, MARGIN + 2, this.y);
    d.text(c2, MARGIN + 70, this.y);
    d.text(c3, MARGIN + 140, this.y);
    this.y += 7;
  }

  /** Bloc total mis en valeur */
  totalBox(label: string, value: string) {
    const d = this.doc;
    this.checkPage();
    this.y += 2;
    d.setFillColor(...GREEN);
    d.roundedRect(MARGIN, this.y, CONTENT_W, 10, 2, 2, 'F');
    d.setTextColor(...WHITE);
    d.setFontSize(11);
    d.setFont('helvetica', 'bold');
    d.text(label, MARGIN + 4, this.y + 7);
    d.text(value, PAGE_W - MARGIN - 4, this.y + 7, { align: 'right' });
    this.y += 14;
  }

  /** Bloc de notes */
  notes(text: string) {
    if (!text) return;
    const d = this.doc;
    this.section('Notes / Observations');
    d.setFillColor(...LGRAY);
    const lines = d.splitTextToSize(text, CONTENT_W - 8) as string[];
    const h = lines.length * 5 + 8;
    d.rect(MARGIN, this.y, CONTENT_W, h, 'F');
    d.setTextColor(...DARK);
    d.setFontSize(9);
    d.setFont('helvetica', 'italic');
    d.text(lines, MARGIN + 4, this.y + 6);
    this.y += h + 4;
  }

  /** Pied de page */
  footer() {
    const d = this.doc;
    const pages = d.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      d.setPage(i);
      d.setFillColor(...LGRAY);
      d.rect(0, PAGE_H - 12, PAGE_W, 12, 'F');
      d.setFillColor(...GREEN);
      d.rect(0, PAGE_H - 12, PAGE_W, 1, 'F');
      d.setTextColor(...GRAY);
      d.setFontSize(7);
      d.setFont('helvetica', 'normal');
      d.text(`${CHURCH} — Document généré le ${new Date().toLocaleDateString('fr-FR')}`, MARGIN, PAGE_H - 5);
      d.text(`Page ${i} / ${pages}`, PAGE_W - MARGIN, PAGE_H - 5, { align: 'right' });
    }
  }

  checkPage() {
    if (this.y > PAGE_H - 25) {
      this.doc.addPage();
      this.y = 20;
    }
  }

  save(filename: string) {
    this.footer();
    this.doc.save(filename);
  }
}

// ─── Export CULTE ─────────────────────────────────────────────────────────────
export function exportWorshipReportPdf(report: WorshipReport, speakerName?: string) {
  const b = new PdfBuilder();
  b.header('Rapport de Culte', fmtDate(report.date));

  b.section('Informations générales');
  b.row('Date', fmtDate(report.date));
  b.row('Cellule', report.cellName, true);
  b.row('Église Source', report.sourceChurch);
  b.row('Thème du message', report.messageTheme, true);
  b.row('Source biblique', report.messageSource);
  if (speakerName) b.row('Prédicateur', speakerName, true);

  b.section('Présence');
  b.table3Row('Catégorie', 'Hommes / Garçons', 'Femmes / Filles', true);
  b.table3Row('Adultes', String(report.attendance.adults.men), String(report.attendance.adults.women));
  b.table3Row('Boss (servis)', String(report.attendance.served.men), String(report.attendance.served.women));
  b.table3Row('Enfants', String(report.attendance.children.boys), String(report.attendance.children.girls));
  b.y += 2;
  b.row('Blooms', String(report.attendance.blooms));
  b.row('Conversions', `H: ${report.attendance.conversions?.men ?? 0}  F: ${report.attendance.conversions?.women ?? 0}`, true);
  b.totalBox('Total participants', String(report.totalParticipants));

  b.section('Nouveaux membres');
  b.table3Row('Catégorie', 'Hommes', 'Femmes', true);
  b.table3Row('Adultes', String(report.newMembers.men), String(report.newMembers.women));
  b.table3Row('Blooms', String(report.newMembers.blooms), '—');
  b.table3Row('Enfants', String(report.newMembers.children), '—');
  b.totalBox('Total nouveaux membres', String(report.totalNewMembers));

  if (report.notes) b.notes(report.notes);

  b.save(`rapport_culte_${report.date.slice(0, 10)}.pdf`);
}

// ─── Export FINANCE ───────────────────────────────────────────────────────────
export function exportFinanceReportPdf(report: FinanceReport) {
  const b = new PdfBuilder();
  b.header('Rapport Financier', fmtDate(report.date));

  b.section('Informations');
  b.row('Date du culte', fmtDate(report.date));

  b.section('Détail des finances');
  b.tableRow('Libellé', 'Montant (FCFA)', true);
  b.tableRow('Dîmes', `${formatNumber(report.finances.tithes)} FCFA`);
  b.tableRow('Offrandes ordinaires', `${formatNumber(report.finances.regularOfferings)} FCFA`);
  b.tableRow('Offrandes spéciales', `${formatNumber(report.finances.specialOfferings)} FCFA`);
  b.totalBox('Total des entrées', `${formatNumber(report.totalFinances)} FCFA`);

  if (report.notes) b.notes(report.notes);

  b.save(`rapport_financier_${report.date.slice(0, 10)}.pdf`);
}

// ─── Export ADN ───────────────────────────────────────────────────────────────
export function exportAdnReportPdf(report: AdnReport) {
  const b = new PdfBuilder();
  b.header('Rapport ADN / Visiteurs', fmtDate(report.date));

  b.section('Informations');
  b.row('Date', fmtDate(report.date));

  b.section('Visiteurs');
  b.table3Row('Catégorie', 'Hommes', 'Femmes', true);
  b.table3Row('Nouveaux visiteurs', String(report.newVisitors.men), String(report.newVisitors.women));
  b.totalBox('Total visiteurs', String(report.totalNewVisitors));

  b.section('Décisions');
  b.tableRow('Type de décision', 'Nombre', true);
  b.tableRow('Veut rejoindre (H)', String(report.visitorDecisions.wantsToJoin.men));
  b.tableRow('Veut rejoindre (F)', String(report.visitorDecisions.wantsToJoin.women));
  b.tableRow('Décision pour Christ (H)', String(report.visitorDecisions.wantsToGiveLifeToJesus.men));
  b.tableRow('Décision pour Christ (F)', String(report.visitorDecisions.wantsToGiveLifeToJesus.women));
  b.tableRow('Indécis', String(report.visitorDecisions.undecided));
  b.y += 2;
  b.totalBox('Veut rejoindre (total)', String(report.totalWantsToJoin));
  b.totalBox('Décision pour Christ (total)', String(report.totalWantsToGiveLifeToJesus));

  if (report.notes) b.notes(report.notes);

  b.save(`rapport_adn_${report.date.slice(0, 10)}.pdf`);
}

// ─── Export ACADÉMIE ──────────────────────────────────────────────────────────
export function exportAcademieReportPdf(report: AcademieReport) {
  const b = new PdfBuilder();
  b.header('Rapport Académie Biblique', fmtDate(report.date));

  b.section('Informations de la session');
  b.row('Date', fmtDate(report.date));
  b.row('Classe', report.className, true);
  b.row('Modérateur', report.moderator);
  b.row('Cours du jour', report.courseOfTheDay, true);
  b.row('Atmosphère spirituelle', report.spiritualAtmosphere);
  b.row('Participation', report.classParticipation, true);

  b.section('Présence');
  b.row('Étudiants inscrits', String(report.actualStudents));
  b.row('Étudiants présents', String(report.presentStudents), true);
  const rate = report.actualStudents > 0
    ? Math.round((report.presentStudents / report.actualStudents) * 100)
    : 0;
  b.totalBox('Taux de présence', `${rate}%`);

  b.section('Prochaine session');
  b.row('Cours prévu', report.nextCourse);
  b.row('Prochain modérateur', report.nextModerator, true);
  b.row('Méditation', report.nextMeditation);
  b.row('Exercice', report.nextExercise, true);

  if (report.needsNotes) b.notes(report.needsNotes);

  b.save(`rapport_academie_${report.date.slice(0, 10)}.pdf`);
}

// ─── Export SAINTE-CÈNE ───────────────────────────────────────────────────────
export function exportSainteCeneReportPdf(report: SainteCeneReport) {
  const b = new PdfBuilder();
  b.header('Rapport Sainte-Cène', fmtDate(report.date));

  b.section('Informations');
  b.row('Date de la célébration', fmtDate(report.date));

  b.section('Pains');
  b.tableRow('Statut', 'Quantité', true);
  b.tableRow('Préparés', String(report.painsPreparees));
  b.tableRow('Distribués', String(report.painsDistribuees));
  b.tableRow('Restants', String(report.painsRestantes));

  b.section('Vins');
  b.tableRow('Statut', 'Quantité', true);
  b.tableRow('Préparés', String(report.vinsPreparees));
  b.tableRow('Distribués', String(report.vinsDistribuees));
  b.tableRow('Restants', String(report.vinsRestantes));

  if (report.needsNotes) b.notes(report.needsNotes);

  b.save(`rapport_sainte_cene_${report.date.slice(0, 10)}.pdf`);
}

// ─── Export SONO ──────────────────────────────────────────────────────────────
const OK_NOK: Record<string, string> = {
  OK: '✓ OK', NOK: '✗ NOK', OUI: 'Oui', NON: 'Non',
  SATISFAISANT: 'Satisfaisant', NON_SATISFAISANT: 'Non satisfaisant',
  BONNE: 'Bonne', MOYENNE: 'Moyenne', MAUVAISE: 'Mauvaise',
  EFFECTUEE: 'Effectuée', NON_EFFECTUEE: 'Non effectuée',
  PROBLEME: '⚠ Problème', EN_COURS: 'En cours',
};
const fmt = (v: string) => OK_NOK[v] ?? v;

export function exportSonoReportPdf(report: SonoReport) {
  const b = new PdfBuilder();
  b.header('Rapport Sono & Médias', fmtDate(report.date));

  b.section('Avant le culte');
  b.tableRow('Vérification', 'Statut', true);
  b.tableRow('Vérification matériel', fmt(report.beforeService.materialCheck));
  b.tableRow('Test qualité son', fmt(report.beforeService.soundQualityTest));
  b.tableRow('Test live streaming', fmt(report.beforeService.liveStreamingTest));
  b.tableRow('Test son en ligne', fmt(report.beforeService.onlineSoundTest));
  b.tableRow('Test vidéo en ligne', fmt(report.beforeService.onlineVideoTest));
  b.tableRow('Préparation équipement photo', fmt(report.beforeService.photoEquipmentPrep));

  b.section('Pendant le culte');
  b.tableRow('Élément', 'Statut', true);
  b.tableRow('Lancement proclamation', fmt(report.duringService.proclamationLaunch));
  b.tableRow('Qualité son salle', fmt(report.duringService.roomSoundQuality));
  b.tableRow('Live streaming', fmt(report.duringService.liveStreaming));
  b.tableRow('Qualité son en ligne', fmt(report.duringService.onlineSoundQuality));
  b.tableRow('Qualité vidéo en ligne', fmt(report.duringService.onlineVideoQuality));
  b.tableRow('Photos pendant culte', fmt(report.duringService.photoshootDuringService));
  if (report.duringService.technicalProblems) {
    b.y += 2;
    b.notes('Problèmes techniques : ' + report.duringService.technicalProblems);
  }

  b.section('Après le culte');
  b.tableRow('Élément', 'Statut', true);
  b.tableRow('Photos servants', fmt(report.afterService.servantsPhotos));
  b.tableRow('Masquage photos', fmt(report.afterService.photoMasking));
  b.tableRow('Publication replay audio', fmt(report.afterService.audioReplayPublication));
  b.tableRow('Publication replay vidéo', fmt(report.afterService.videoReplayPublication));
  b.tableRow('Archivage fichiers', fmt(report.afterService.fileArchiving));

  if (report.generalObservations) b.notes(report.generalObservations);

  b.save(`rapport_sono_${report.date.slice(0, 10)}.pdf`);
}
